import { QueryAtom } from "@/api/types/query.types";
import { atom } from "jotai";
import { MetricCode, MetricCodeWithFallbacks, Metrics } from "../Metrics";
import {
    MetricType,
    PeriodResolution,
    PeriodWindow,
    WithDeltaCalculator,
} from "../Metrics.types";
import { PeriodRange } from "../PeriodRange";
import { getDefaultAsOfDate } from "../helpers/asOfDate";
import dayjs from "dayjs";
import { DeltaCalculator } from "../Delta";
import { HydratedMetric, HydratedMetricObject } from "../HydratedMetric";
import { METRIC_CONFIG } from "../METRIC_CONFIG";
import {
    balanceSheetAtom,
    financeMetricsAtom,
    finLabMetricsAtom,
    incomeStatementAtom,
} from "./MetricQueries.state";
import { Period } from "../Period";

export const availableDataRangeAtom = atom<QueryAtom<PeriodRange>>((get) => {
    const incomeStatement = get(incomeStatementAtom);
    const balanceSheet = get(balanceSheetAtom);
    const isLoading = incomeStatement.isLoading || balanceSheet.isLoading;

    if (!incomeStatement.data || !balanceSheet.data)
        return {
            data: null,
            isLoading,
            isError: incomeStatement.isError || balanceSheet.isError,
        };

    const startingISDate = incomeStatement.data?.startingPeriod;
    const startingBSDate = balanceSheet.data?.startingPeriod;
    const endingISDate = incomeStatement.data?.endingPeriod;
    const endingBSDate = balanceSheet.data?.endingPeriod;

    const startingDate = dayjs(startingISDate).isBefore(dayjs(startingBSDate))
        ? dayjs(startingISDate)
        : dayjs(startingBSDate);
    const endingDate = dayjs(endingISDate).isBefore(dayjs(endingBSDate))
        ? dayjs(endingISDate)
        : dayjs(endingBSDate);

    const range = new PeriodRange(
        new Period(startingDate),
        new Period(endingDate),
    );

    return {
        data: range,
        isLoading: false,
        isError: false,
    };
});

export const asOfDateAtom = atom<dayjs.Dayjs>((get) => {
    const availableDataRange = get(availableDataRangeAtom);
    const asOfDate = availableDataRange.data?.to.base;
    return asOfDate?.isValid() ? asOfDate : getDefaultAsOfDate();
});

export const metricsAtom = atom<QueryAtom<Metrics>>((get) => {
    const financeMetrics = get(financeMetricsAtom);
    const finLabMetrics = get(finLabMetricsAtom);

    const data = {
        monthly: financeMetrics.data?.monthly.concat(
            finLabMetrics.data?.monthly || [],
        ),
        quarterly: financeMetrics.data?.quarterly.concat(
            finLabMetrics.data?.quarterly || [],
        ),
        yearly: financeMetrics.data?.yearly.concat(
            finLabMetrics.data?.yearly || [],
        ),
    };

    const metrics = new Metrics({
        monthly: data.monthly || [],
        quarterly: data.quarterly || [],
        yearly: data.yearly || [],
    });

    return {
        isLoading: financeMetrics.isLoading || finLabMetrics.isLoading,
        isError: financeMetrics.isError || finLabMetrics.isError,
        data: metrics,
    };
});

export const getPeriodsAtom = atom((get) => {
    const metrics = get(metricsAtom);
    const asOfDate = get(asOfDateAtom);
    return (
        window: PeriodWindow,
        isYTD: boolean,
        isAll: boolean,
        codes: MetricCodeWithFallbacks[],
    ): PeriodRange | null => {
        const range = PeriodRange.fromDate(asOfDate, window);

        if (!range) return null;

        if (isYTD) {
            console.log(range.getYTDPeriods(asOfDate));
            return PeriodRange.fromArray(range?.getYTDPeriods(asOfDate) ?? []);
        }

        if (isAll) {
            return (
                metrics.data?.getGlobalPeriodRange(window.resolution, codes) ??
                null
            );
        }

        return range;
    };
});

export const getMetricsByCodeAtom = atom((get) => {
    const allMetrics = get(metricsAtom);
    return (
        codes: MetricCodeWithFallbacks[],
        resolution: PeriodResolution,
    ): Map<MetricCode, HydratedMetric | null> => {
        const map = new Map<MetricCode, HydratedMetric | null>();
        for (const code of codes) {
            const metric = allMetrics.data?.getMetric(code, resolution);
            if (metric) {
                map.set(metric.code, metric);
            } else {
                map.set(code[0] as MetricCode, null);
            }
        }
        return map;
    };
});

export const getTabularMetricsByCodeAtom = atom((get) => {
    const getMetricsByCode = get(getMetricsByCodeAtom);
    return (
        codes: MetricCodeWithFallbacks[],
        resolution: PeriodResolution,
    ): HydratedMetricObject[] => {
        const metrics = getMetricsByCode(codes, resolution);
        if (!metrics) return [];
        return Array.from(metrics.entries())
            .map(([code, metric]) => {
                if (metric) return metric.toTabular();
                const config = METRIC_CONFIG[code];
                if (!config) return null;
                return {
                    code,
                    label: config.label,
                    source: config.source,
                    type: config.type,
                    periodRange: null,
                } as HydratedMetricObject;
            })
            .filter((metric) => metric !== null);
    };
});

export const getDeltasByCodeAtom = atom((get) => {
    const getMetricsByCode = get(getMetricsByCodeAtom);
    const asOfDate = get(asOfDateAtom);

    return (
        codes: MetricCodeWithFallbacks[],
        resolution: PeriodResolution,
    ): Map<MetricCode, DeltaCalculator> | null => {
        const metrics = getMetricsByCode(codes, resolution);
        if (!metrics) return null;
        return codes.reduce((acc, code) => {
            const codeArray = Array.isArray(code) ? code : [code];
            const metric = codeArray
                .map((c) => metrics.get(c))
                .find((m) => m !== null && m !== undefined);
            if (!metric || metric.type === MetricType.PERCENTAGE) return acc;
            acc.set(metric.code, new DeltaCalculator(metric, asOfDate));
            return acc;
        }, new Map<MetricCode, DeltaCalculator>());
    };
});

export const getTableDataAtom = atom((get) => {
    const getTabularMetricsByCode = get(getTabularMetricsByCodeAtom);
    const getDeltasByCode = get(getDeltasByCodeAtom);
    return (
        codes: MetricCodeWithFallbacks[],
        resolution: PeriodResolution,
    ): WithDeltaCalculator<HydratedMetricObject>[] => {
        const metrics = getTabularMetricsByCode(codes, resolution);
        const deltas = getDeltasByCode(codes, resolution);

        const metricsMap = new Map(metrics.map((m) => [m.code, m]));

        return codes.map((codeOrFallbacks) => {
            const codeArray = Array.isArray(codeOrFallbacks)
                ? codeOrFallbacks
                : [codeOrFallbacks];
            const primaryCode = codeArray[0] as MetricCode;

            const metric =
                codeArray
                    .map((c) => metricsMap.get(c as MetricCode))
                    .find((m) => m !== undefined) ||
                ({
                    code: primaryCode,
                    label: METRIC_CONFIG[primaryCode]?.label || primaryCode,
                    source: METRIC_CONFIG[primaryCode]?.source,
                    type: METRIC_CONFIG[primaryCode]?.type,
                    periodRange: null,
                } as HydratedMetricObject);

            return {
                ...metric,
                deltaCalculator: deltas?.get(metric.code) || null,
            } as unknown as WithDeltaCalculator<HydratedMetricObject>;
        });
    };
});
